import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import 'react-quill-new/dist/quill.snow.css';
import Menu from './Components/Menu';
import Header from './Components/Header';
import { config } from './Components/Config';
import { format } from "date-fns";
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RcTooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const GOLD = '#F9D876';
const PIE_COLORS = ['#F9D876', '#4A90D9', '#E25C5C', '#5CB85C', '#9B59B6', '#F39C12', '#1ABC9C', '#E74C3C', '#3498DB', '#95A5A6'];

const TooltipInfo = ({ text }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{text}</Tooltip>}
    >
        <span className="ms-1" style={{ cursor: 'pointer' }}>
            <i className="bi bi-info-circle text-secondary" style={{ fontSize: '0.8rem' }}></i>
        </span>
    </OverlayTrigger>
);

const DetailEvent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({});
    const [activeTab, setActiveTab] = useState('general');
    const [formSubTab, setFormSubTab] = useState('feedback');
    const [dashboardSubTab, setDashboardSubTab] = useState('feedback');
    const [tableSearch, setTableSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const crmUser = JSON.parse(localStorage.getItem('crmUser'));

    // Participant search debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch]);

    const [participants, setParticipants] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    // Participant custom answers modal
    const [selectedParticipantAnswers, setSelectedParticipantAnswers] = useState(null);

    // Modals state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmActionType, setConfirmActionType] = useState(''); // 'approve', 'reject', 'feedback'
    const [actionParticipantIds, setActionParticipantIds] = useState([]);
    const [isActionLoading, setIsActionLoading] = useState(false);

    // Section tab (participants vs feedback)
    const [sectionTab, setSectionTab] = useState('participants');

    // Feedback list state
    const [feedbacks, setFeedbacks] = useState([]);
    const [feedbackSearch, setFeedbackSearch] = useState('');
    const [debouncedFeedbackSearch, setDebouncedFeedbackSearch] = useState('');
    const [feedbackPagination, setFeedbackPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    // Dashboard state
    const [feedbackSummary, setFeedbackSummary] = useState([]);
    const [feedbackSummaryTotal, setFeedbackSummaryTotal] = useState(0);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [registrationSummary, setRegistrationSummary] = useState([]);
    const [registrationSummaryTotal, setRegistrationSummaryTotal] = useState(0);
    const [isRegSummaryLoading, setIsRegSummaryLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFeedbackSearch(feedbackSearch), 500);
        return () => clearTimeout(timer);
    }, [feedbackSearch]);

    useEffect(() => {
        if (id) {
            fetchEventDetail();
            fetchParticipants();
        }
    }, [id]);

    useEffect(() => {
        fetchParticipants();
    }, [pagination.page, pagination.limit, debouncedSearch, statusFilter]);

    useEffect(() => {
        if (sectionTab === 'feedback' && id) fetchFeedbacks();
    }, [sectionTab, feedbackPagination.page, feedbackPagination.limit, debouncedFeedbackSearch]);

    useEffect(() => {
        if (activeTab === 'dashboard' && id) {
            if (dashboardSubTab === 'feedback') fetchFeedbackSummary();
            if (dashboardSubTab === 'registration') fetchRegistrationSummary();
        }
    }, [activeTab, dashboardSubTab]);

    const fetchParticipants = async () => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search: debouncedSearch
            });

            if (statusFilter) {
                queryParams.append('status', statusFilter);
            }

            const response = await fetch(`${config.EVENT_ENDPOINT}event/${id}/participants?${queryParams}`);
            const result = await response.json();

            if (result.success) {
                setParticipants(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: result.meta?.totalItems || result.data.length,
                    totalPages: result.meta?.totalPages || Math.ceil(result.data.length / prev.limit)
                }));
            } else {
                console.error("Failed to fetch participants");
            }
        } catch (error) {
            console.error("Error fetching participants:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchFeedbacks = async () => {
        setIsFeedbackLoading(true);
        try {
            const params = new URLSearchParams({
                page: feedbackPagination.page,
                limit: feedbackPagination.limit,
                search: debouncedFeedbackSearch,
            });
            const response = await fetch(`${config.EVENT_ENDPOINT}feedback/event/${id}?${params}`);
            const result = await response.json();
            if (result.success) {
                setFeedbacks(result.data || []);
                setFeedbackPagination(prev => ({
                    ...prev,
                    total: result.meta?.total || 0,
                    totalPages: result.meta?.totalPages || 0,
                }));
            }
        } catch (error) {
            console.error("Error fetching feedbacks:", error);
        } finally {
            setIsFeedbackLoading(false);
        }
    };

    const openFeedbackDetail = async (feedbackId) => {
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}feedback/${feedbackId}`);
            const result = await response.json();
            if (result.success) {
                setSelectedFeedback(result.data);
                setShowFeedbackModal(true);
            }
        } catch (error) {
            console.error("Error fetching feedback detail:", error);
        }
    };

    const fetchFeedbackSummary = async () => {
        setIsSummaryLoading(true);
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}feedback/event/${id}/summary`);
            const result = await response.json();
            if (result.success) {
                setFeedbackSummary(result.data || []);
                setFeedbackSummaryTotal(result.totalResponses || 0);
            }
        } catch (error) {
            console.error("Error fetching feedback summary:", error);
        } finally {
            setIsSummaryLoading(false);
        }
    };

    const fetchRegistrationSummary = async () => {
        setIsRegSummaryLoading(true);
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}register/event/${id}/summary`);
            const result = await response.json();
            if (result.success) {
                setRegistrationSummary(result.data.summary || []);
                setRegistrationSummaryTotal(result.data.total || 0);
            }
        } catch (error) {
            console.error("Error fetching registration summary:", error);
        } finally {
            setIsRegSummaryLoading(false);
        }
    };

    const getQuestionDef = (questionId) =>
        (formData.feedbackFormQuestions || []).find(q => q.id === questionId);

    const aggregateChart = (values, type, questionId) => {
        if (type === 'radio') {
            const counts = {};
            values.forEach(v => { if (v) counts[v] = (counts[v] || 0) + 1; });
            return Object.entries(counts).map(([name, value]) => ({ name, value }));
        }
        if (type === 'checkbox') {
            const counts = {};
            values.forEach(arr => { (Array.isArray(arr) ? arr : []).forEach(v => { if (v) counts[v] = (counts[v] || 0) + 1; }); });
            return Object.entries(counts).map(([name, value]) => ({ name, value }));
        }
        if (type === 'linear_scale') {
            const qDef = getQuestionDef(questionId);
            const min = qDef?.scaleMin ?? 1;
            const max = qDef?.scaleMax ?? 5;
            const counts = {};
            for (let i = min; i <= max; i++) counts[i] = 0;
            values.forEach(v => { const n = Number(v); if (counts[n] !== undefined) counts[n]++; });
            return Object.entries(counts).map(([name, value]) => ({ name, value: Number(value) }));
        }
        return [];
    };

    const renderFeedbackPaginationItems = () => {
        const { page, totalPages } = feedbackPagination;
        const items = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) items.push(i);
        } else {
            if (page <= 4) { for (let i = 1; i <= 5; i++) items.push(i); items.push('...'); items.push(totalPages); }
            else if (page >= totalPages - 3) { items.push(1); items.push('...'); for (let i = totalPages - 4; i <= totalPages; i++) items.push(i); }
            else { items.push(1); items.push('...'); items.push(page - 1); items.push(page); items.push(page + 1); items.push('...'); items.push(totalPages); }
        }
        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${feedbackPagination.page === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => setFeedbackPagination(prev => ({ ...prev, page: item }))}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    const fetchEventDetail = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}event/${id}`);
            const result = await response.json();

            if (result.success) {
                const data = result.data;
                const rawQ = data.feedbackFormQuestions;
                let parsedQ = [];
                if (Array.isArray(rawQ)) parsedQ = rawQ;
                else if (typeof rawQ === 'string') { try { parsedQ = JSON.parse(rawQ); } catch { parsedQ = []; } }
                setFormData({
                    name: data.name || '',
                    description: data.description || '',
                    promptAi: data.promptAi || '',
                    type: data.type || 'ON_SITE',
                    address: data.address || '',
                    addressLink: data.addressLink || '',
                    timeType: data.timeType || 'WIB',
                    link: data.link || '',
                    image: data.image || '',
                    feedbackLink: data.feedbackLink || '',
                    startDate: data.startDate,
                    endDate: data.endDate,
                    status: data.status || 'UPCOMING',
                    checkInManual: data.checkInManual || false,
                    checkInQr: data.checkInQr || false,
                    checkInFace: data.checkInFace || false,
                    invitationSubject: data.invitationSubject || '',
                    invitationText: data.invitationText || '',
                    waitingSubject: data.waitingSubject || '',
                    waitingText: data.waitingText || '',
                    acceptSubject: data.acceptSubject || '',
                    acceptText: data.acceptText || '',
                    rejectSubject: data.rejectSubject || '',
                    rejectText: data.rejectText || '',
                    feedbackSubject: data.feedbackSubject || '',
                    feedbackText: data.feedbackText || '',
                    calenderText: data.calenderText || '',
                    show: data.show,
                    feedbackFormDescription: data.feedbackFormDescription || '',
                    feedbackFormImage: data.feedbackFormImage || '',
                    feedbackFormQuestions: parsedQ,
                    registrationFormDescription: data.registrationFormDescription || '',
                    registrationFormQuestions: (() => {
                        const rawRQ = data.registrationFormQuestions;
                        if (!rawRQ) return [];
                        if (Array.isArray(rawRQ)) return rawRQ;
                        try { const p = JSON.parse(rawRQ); return Array.isArray(p) ? p : []; } catch { return []; }
                    })(),
                });
            } else {
                console.error("Failed to fetch event detail");
            }
        } catch (error) {
            console.error("Error fetching event detail:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Setup pagination for participants
        setPagination(prev => ({
            ...prev,
            total: pagination.total,
            totalPages: Math.ceil(pagination.total / prev.limit)
        }));
    }, [pagination.limit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Saving Detail Event Data:", formData);
    };

    const renderPaginationItems = () => {
        const { page, totalPages } = pagination;
        const items = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (page >= totalPages - 3) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                items.push(page - 1);
                items.push(page);
                items.push(page + 1);
                items.push('...');
                items.push(totalPages);
            }
        }

        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${pagination.page === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => setPagination(prev => ({ ...prev, page: item }))}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pendingParticipants = participants.filter(p => p.allowAttend === null);
            if (pendingParticipants.length > 0) {
                setSelectedParticipants(pendingParticipants.map(p => p.id));
            } else {
                const approvedParticipants = participants.filter(p => p.allowAttend === true);
                setSelectedParticipants(approvedParticipants.map(p => p.id));
            }
        } else {
            setSelectedParticipants([]);
        }
    };

    const handleSelectParticipant = (e, participantId) => {
        if (e.target.checked) {
            setSelectedParticipants(prev => [...prev, participantId]);
        } else {
            setSelectedParticipants(prev => prev.filter(id => id !== participantId));
        }
    };

    const hasProcessedSelected = selectedParticipants.some(id => {
        const p = participants.find(part => part.id === id);
        return p && p.allowAttend !== null;
    });

    const areAllSelectedPresent = selectedParticipants.length > 0 && selectedParticipants.every(id => {
        const p = participants.find(part => part.id === id);
        return p && p.allowAttend === true && p.attended === true;
    });

    const handleBulkAction = (actionType) => {
        if (selectedParticipants.length === 0) return;
        setConfirmActionType(actionType);
        setActionParticipantIds(selectedParticipants);
        setShowConfirmModal(true);
    };

    const handleSingleAction = (actionType, participantId) => {
        setConfirmActionType(actionType);
        setActionParticipantIds([participantId]);
        setShowConfirmModal(true);
    };

    const handleExportData = async () => {
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}event/${id}/participants/download`, {
                method: 'GET',
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `event_participants.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else {
                console.error("Failed to export participants data");
            }
        } catch (error) {
            console.error("Error exporting participants data:", error);
        }
    };

    const handleExportFeedbackData = async () => {
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}feedback/event/${id}/download`, {
                method: 'GET',
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `event_feedbacks.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else {
                console.error("Failed to export feedbacks data");
            }
        } catch (error) {
            console.error("Error exporting feedbacks data:", error);
        }
    };

    const executeConfirmAction = async () => {
        try {
            setIsActionLoading(true);
            if (confirmActionType === 'approve' || confirmActionType === 'reject') {
                const allowAttend = confirmActionType === 'approve';
                const response = await fetch(`${config.EVENT_ENDPOINT}event/${id}/participants/allow-attend`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        participantIds: actionParticipantIds,
                        allowAttend: allowAttend
                    })
                });

                const result = await response.json();
                if (result.success || response.ok) {
                    fetchParticipants();
                    setSelectedParticipants([]);
                } else {
                    console.error("Action failed:", result.message);
                }
            } else if (confirmActionType === 'feedback') {
                const promises = actionParticipantIds.map(participantId =>
                    fetch(`${config.EVENT_ENDPOINT}event/${id}/participants/${participantId}/send-feedback`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' }
                    })
                );
                await Promise.all(promises);
                fetchParticipants();
                setSelectedParticipants([]);
            } else if (confirmActionType === 'checkin') {
                const promises = actionParticipantIds.map(participantId =>
                    fetch(`${config.EVENT_ENDPOINT}event/${id}/participants/${participantId}/manual-checkin`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' }
                    })
                );
                await Promise.all(promises);
                fetchParticipants();
                setSelectedParticipants([]);
            }
        } catch (error) {
            console.error("Action error:", error);
        } finally {
            setIsActionLoading(false);
            setShowConfirmModal(false);
        }
    };

    return (
        <div id="event-detail">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0" />
                        <Menu active="event" />
                    </div>
                    <div className="col-md-10 flex-grow-1" >
                        <Header />
                        <hr className="m-0" />
                        <div className="main">
                            <div className="p-3 p-md-4">
                                {/* Breadcrumb */}
                                <div className="d-flex align-items-center text-secondary mb-4">
                                    <span className="cursor-pointer hover-text-dark" onClick={() => navigate('/event')} style={{ cursor: 'pointer' }}>Event</span>
                                    <i className="bi bi-chevron-right mx-2 fs-6"></i>
                                    <span className="text-dark">Event Detail</span>
                                </div>

                                <h3 className="mb-4 fw-bold">Event Detail</h3>

                                {/* Detail Form Section */}
                                <div className="bg-white rounded-3 shadow-sm mb-5 p-4">
                                    <div className="d-flex border-bottom mb-4 overflow-auto flex-nowrap">
                                        <button
                                            className={`btn px-3 px-md-4 py-2 py-md-3 border-0 rounded-0 fw-semibold text-nowrap ${activeTab === 'general' ? 'border-bottom border-2 border-warning' : 'text-secondary bg-transparent'}`}
                                            onClick={() => setActiveTab('general')}
                                        >
                                            General Information
                                        </button>
                                        <button
                                            className={`btn px-3 px-md-4 py-2 py-md-3 border-0 rounded-0 fw-semibold text-nowrap ${activeTab === 'templates' ? 'border-bottom border-2 border-warning' : 'text-secondary bg-transparent'}`}
                                            onClick={() => setActiveTab('templates')}
                                        >
                                            Email Templates
                                        </button>
                                        <button
                                            className={`btn px-3 px-md-4 py-2 py-md-3 border-0 rounded-0 fw-semibold text-nowrap ${activeTab === 'feedback' ? 'border-bottom border-2 border-warning' : 'text-secondary bg-transparent'}`}
                                            onClick={() => setActiveTab('feedback')}
                                        >
                                            Form
                                        </button>
                                        <button
                                            className={`btn px-3 px-md-4 py-2 py-md-3 border-0 rounded-0 fw-semibold text-nowrap ${activeTab === 'dashboard' ? 'border-bottom border-2 border-warning' : 'text-secondary bg-transparent'}`}
                                            onClick={() => setActiveTab('dashboard')}
                                        >
                                            Dashboard
                                        </button>
                                    </div>

                                    <form id="event-detail-form" onSubmit={handleSubmit}>
                                        {activeTab === 'general' && (
                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Event Name <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        disabled
                                                        className="form-control p-2 bg-light"
                                                        placeholder="Enter event name"
                                                    />
                                                </div>

                                                <div className="col-md-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Description <span className="text-danger">*</span>
                                                    </label>
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.description}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        style={{ height: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', backgroundColor: '#f8f9fa' }}
                                                    />
                                                </div>

                                                <div className="col-md-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Prompt AI
                                                    </label>
                                                    <textarea
                                                        name="promptAi"
                                                        value={formData.promptAi}
                                                        readOnly
                                                        className="form-control p-2 font-monospace bg-light"
                                                        rows="5"
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Event Type <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        name="type"
                                                        value={formData.type}
                                                        disabled
                                                        className="form-select p-2 bg-light"
                                                    >
                                                        <option value="ON_SITE">Offline</option>
                                                        <option value="ONLINE">Online</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Event Visibility <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        name="show"
                                                        value={formData.show}
                                                        disabled
                                                        className="form-select p-2 bg-light"
                                                    >
                                                        <option>Not Selected</option>
                                                        <option value="PUBLIC">Public</option>
                                                        <option value="PRIVATE">Private</option>
                                                    </select>
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Check-In Type
                                                    </label>
                                                    <div className="d-flex gap-4">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="checkInManual"
                                                                name="checkInManual"
                                                                checked={!!formData.checkInManual}
                                                                disabled
                                                            />
                                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInManual">
                                                                Manual
                                                                <TooltipInfo text="The event admin manually searches for the participant's registration and marks them as attended upon arrival." />
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="checkInQr"
                                                                name="checkInQr"
                                                                checked={!!formData.checkInQr}
                                                                disabled
                                                            />
                                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInQr">
                                                                QR Scan
                                                                <TooltipInfo text="Approved participants receive a unique QR code via email. They present it at the venue to be scanned for instant check-in." />
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="checkInFace"
                                                                name="checkInFace"
                                                                checked={!!formData.checkInFace}
                                                                disabled
                                                            />
                                                            <label className="form-check-label d-flex align-items-center gap-1" htmlFor="checkInFace">
                                                                Face Recognition
                                                                <TooltipInfo text="Participants register their face in advance. On event day, they simply look at the camera to check in automatically — no QR code or admin needed." />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Address / Location <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="address"
                                                        value={formData.address}
                                                        disabled
                                                        className="form-control p-2 bg-light"
                                                        placeholder="Location or Address"
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Link Address
                                                    </label>
                                                    <input
                                                        type="url"
                                                        name="addressLink"
                                                        value={formData.addressLink}
                                                        disabled
                                                        placeholder="e.g. https://maps.google.com/..."
                                                        className="form-control p-2 bg-light"
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">Event Link (Online Only)</label>
                                                    <input
                                                        type="text"
                                                        name="link"
                                                        disabled
                                                        value={formData.link}
                                                        className="form-control p-2 bg-light"
                                                        placeholder="Enter event URL"
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Feedback Link (Required After Event) <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="feedbackLink"
                                                        value={formData.feedbackLink}
                                                        disabled
                                                        className="form-control p-2 bg-light"
                                                        placeholder="Enter feedback URL"
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Image
                                                    </label>
                                                    {formData.image ? (
                                                        <div >
                                                            <img
                                                                src={config.EVENT_ENDPOINT + 'event/image/' + formData.image}
                                                                alt="Current banner"
                                                                className="img-thumbnail"
                                                                style={{ maxHeight: '200px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="text-secondary fst-italic">No image uploaded</div>
                                                    )}
                                                </div>


                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Start Date & Time <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        name="startDate"
                                                        value={formData.startDate ? format(new Date(formData.startDate), 'yyyy-MM-dd HH:mm') : ''}
                                                        disabled
                                                        className="form-control p-2 bg-light"
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        End Date & Time <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="datetime-local"
                                                        name="endDate"
                                                        value={formData.endDate ? format(new Date(formData.endDate), 'yyyy-MM-dd HH:mm') : ''}
                                                        disabled
                                                        className="form-control p-2 bg-light"
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Time Type <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        name="timeType"
                                                        value={formData.timeType}
                                                        disabled
                                                        className="form-select p-2 bg-light"
                                                    >
                                                        <option value="WIB">WIB (Western Indonesia Time)</option>
                                                        <option value="WITA">WITA (Central Indonesia Time)</option>
                                                        <option value="WIT">WIT (Eastern Indonesia Time)</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label text-secondary fw-semibold mb-2">
                                                        Status <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        name="status"
                                                        value={formData.status}
                                                        disabled
                                                        className="form-select p-2 bg-light"
                                                    >
                                                        <option value="UPCOMING">Upcoming</option>
                                                        <option value="ON_PROGRESS">On Progress</option>
                                                        <option value="DONE">Done</option>
                                                        <option value="CANCELED">Canceled</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {activeTab === 'templates' && (
                                            <div className="d-flex flex-column gap-4">
                                                <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Email Invitation Template
                                                        <TooltipInfo text="Template message for email invitation participants" />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="invitationSubject"
                                                        value={formData.invitationSubject}
                                                        disabled
                                                        className="form-control p-2 mb-3 bg-light"
                                                        placeholder="Email Invitation Subject"
                                                    />
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.invitationText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Email Invitation Text..."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Registration Confirmation Template
                                                        <TooltipInfo text="Template message for waiting participants" />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="waitingSubject"
                                                        value={formData.waitingSubject}
                                                        disabled
                                                        className="form-control p-2 mb-3 bg-light"
                                                        placeholder="Registration Confirmation Subject"
                                                    />
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.waitingText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Registration Confirmation Text..."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Approval Confirmation Template
                                                        <TooltipInfo text="Template message for accepted participants in the event" />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="acceptSubject"
                                                        value={formData.acceptSubject}
                                                        disabled
                                                        className="form-control p-2 mb-3 bg-light"
                                                        placeholder="Approval Confirmation Subject"
                                                    />
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.acceptText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Approval Confirmation Text..."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Rejection Confirmation Template
                                                        <TooltipInfo text="Template message for rejected participants in the event" />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="rejectSubject"
                                                        value={formData.rejectSubject}
                                                        disabled
                                                        className="form-control p-2 mb-3 bg-light"
                                                        placeholder="Rejection Confirmation Subject"
                                                    />
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.rejectText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Rejection Confirmation Text..."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Feedback Request Template
                                                        <TooltipInfo text="Template message for feedback invitation" />
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="feedbackSubject"
                                                        value={formData.feedbackSubject}
                                                        disabled
                                                        className="form-control p-2 mb-3 bg-light"
                                                        placeholder="Post-Event Subject"
                                                    />
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.feedbackText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Post-Event Text..."
                                                    />
                                                </div>

                                                {/* <div>
                                                    <label className="form-label text-secondary fw-semibold mb-2 d-flex align-items-center">
                                                        Calendar Text
                                                        <TooltipInfo text="Template message for event invitation calendar" />
                                                    </label>
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.calenderText}
                                                        readOnly={true}
                                                        modules={{ toolbar: false }}
                                                        className="bg-light"
                                                        placeholder="Calendar Text..."
                                                    />
                                                </div> */}
                                            </div>
                                        )}

                                        {activeTab === 'dashboard' && (
                                            <div>
                                                {/* Dashboard sub-tabs */}
                                                <div className="d-flex gap-2 mb-4">
                                                    <button
                                                        className={`btn btn-sm fw-semibold px-3 py-2 ${dashboardSubTab === 'feedback' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                                        onClick={() => setDashboardSubTab('feedback')}
                                                    >
                                                        <i className="bi bi-chat-square-text me-1"></i>Feedback
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm fw-semibold px-3 py-2 ${dashboardSubTab === 'registration' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                                        onClick={() => setDashboardSubTab('registration')}
                                                    >
                                                        <i className="bi bi-person-lines-fill me-1"></i>Registration
                                                    </button>
                                                </div>

                                                {dashboardSubTab === 'feedback' && (isSummaryLoading ? (
                                                    <div className="text-center py-5 text-secondary">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status" />
                                                        Loading dashboard...
                                                    </div>
                                                ) : feedbackSummary.length === 0 ? (
                                                    <div className="text-center py-5 text-secondary fst-italic">
                                                        <i className="bi bi-bar-chart-line fs-2 d-block mb-2"></i>
                                                        No feedback responses yet.
                                                    </div>
                                                ) : (
                                                    <div className="d-flex flex-column gap-4">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <span className="badge bg-gold text-dark fw-semibold px-3 py-2 fs-6">{feedbackSummaryTotal} Total Responses</span>
                                                        </div>
                                                        {feedbackSummary.map((q, idx) => {
                                                            const qDef = getQuestionDef(q.questionId);
                                                            const responseCount = q.values.length;
                                                            const chartData = aggregateChart(q.values, q.type, q.questionId);

                                                            return (
                                                                <div key={q.questionId || idx} className="border rounded-3 p-4 bg-white">
                                                                    <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
                                                                        <div className="d-flex gap-2 align-items-start">
                                                                            <span className="badge bg-warning text-dark flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}>{idx + 1}</span>
                                                                            <span className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>{q.question}</span>
                                                                        </div>
                                                                        <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                                                            {q.type === 'short_text' ? 'Short Text' : q.type === 'radio' ? 'Multiple Choice' : q.type === 'checkbox' ? 'Checkbox' : 'Linear Scale'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-secondary small mb-3">{responseCount} responses</p>

                                                                    {/* SHORT TEXT */}
                                                                    {q.type === 'short_text' && (
                                                                        <div style={{ maxHeight: '320px', overflowY: 'auto' }} className="d-flex flex-column gap-2">
                                                                            {q.values.filter(v => v && String(v).trim()).map((v, i) => (
                                                                                <div key={i} className="p-3 rounded-2 text-dark" style={{ backgroundColor: '#f8f9fa', fontSize: '0.9rem' }}>{v}</div>
                                                                            ))}
                                                                            {q.values.filter(v => v && String(v).trim()).length === 0 && (
                                                                                <span className="text-secondary fst-italic small">No text answers.</span>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* RADIO → PIE CHART */}
                                                                    {q.type === 'radio' && chartData.length > 0 && (
                                                                        <ResponsiveContainer width="100%" height={280}>
                                                                            <PieChart>
                                                                                <Pie
                                                                                    data={chartData}
                                                                                    cx="40%"
                                                                                    cy="50%"
                                                                                    outerRadius={100}
                                                                                    dataKey="value"
                                                                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                                                                    labelLine={false}
                                                                                >
                                                                                    {chartData.map((entry, i) => (
                                                                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                                                    ))}
                                                                                </Pie>
                                                                                <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" />
                                                                            </PieChart>
                                                                        </ResponsiveContainer>
                                                                    )}

                                                                    {/* CHECKBOX → HORIZONTAL BAR */}
                                                                    {q.type === 'checkbox' && chartData.length > 0 && (
                                                                        <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 52)}>
                                                                            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 10, bottom: 0 }}>
                                                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                                                                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                                                                                <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                <Bar dataKey="value" fill={GOLD} radius={[0, 4, 4, 0]}
                                                                                    label={{ position: 'right', fontSize: 12, formatter: (v) => `${v} (${((v / responseCount) * 100).toFixed(0)}%)` }}
                                                                                />
                                                                            </BarChart>
                                                                        </ResponsiveContainer>
                                                                    )}

                                                                    {/* LINEAR SCALE → VERTICAL BAR */}
                                                                    {q.type === 'linear_scale' && chartData.length > 0 && (
                                                                        <>
                                                                            <ResponsiveContainer width="100%" height={260}>
                                                                                <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                                                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                                    <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                                                                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                                                                    <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                    <Bar dataKey="value" fill={GOLD} radius={[4, 4, 0, 0]}
                                                                                        label={{ position: 'top', fontSize: 11, formatter: (v) => v > 0 ? `${v} (${((v / responseCount) * 100).toFixed(0)}%)` : '' }}
                                                                                    />
                                                                                </BarChart>
                                                                            </ResponsiveContainer>
                                                                            {(qDef?.scaleMinLabel || qDef?.scaleMaxLabel) && (
                                                                                <div className="d-flex justify-content-between text-secondary mt-1 px-1" style={{ fontSize: '0.78rem' }}>
                                                                                    <span>{qDef?.scaleMin ?? 1} — {qDef?.scaleMinLabel}</span>
                                                                                    <span>{qDef?.scaleMax ?? 5} — {qDef?.scaleMaxLabel}</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))}

                                                {dashboardSubTab === 'registration' && (isRegSummaryLoading ? (
                                                    <div className="text-center py-5 text-secondary">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status" />
                                                        Loading dashboard...
                                                    </div>
                                                ) : registrationSummary.length === 0 ? (
                                                    <div className="text-center py-5 text-secondary fst-italic">
                                                        <i className="bi bi-person-lines-fill fs-2 d-block mb-2"></i>
                                                        No registration responses yet.
                                                    </div>
                                                ) : (
                                                    <div className="d-flex flex-column gap-4">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <span className="badge bg-gold text-dark fw-semibold px-3 py-2 fs-6">{registrationSummaryTotal} Total Registrations</span>
                                                        </div>
                                                        {registrationSummary.map((q, idx) => {
                                                            const responseCount = q.values.length;
                                                            const chartData = aggregateChart(q.values, q.type, q.questionId);

                                                            return (
                                                                <div key={q.questionId || idx} className="border rounded-3 p-4 bg-white">
                                                                    <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
                                                                        <div className="d-flex gap-2 align-items-start">
                                                                            <span className="badge bg-warning text-dark flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}>{idx + 1}</span>
                                                                            <span className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>{q.question}</span>
                                                                        </div>
                                                                        <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                                                            {q.type === 'short_text' ? 'Short Text' : q.type === 'radio' ? 'Multiple Choice' : q.type === 'checkbox' ? 'Checkbox' : 'Linear Scale'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-secondary small mb-3">{responseCount} responses</p>

                                                                    {q.type === 'short_text' && (
                                                                        <div style={{ maxHeight: '320px', overflowY: 'auto' }} className="d-flex flex-column gap-2">
                                                                            {q.values.filter(v => v && String(v).trim()).map((v, i) => (
                                                                                <div key={i} className="p-3 rounded-2 text-dark" style={{ backgroundColor: '#f8f9fa', fontSize: '0.9rem' }}>{v}</div>
                                                                            ))}
                                                                            {q.values.filter(v => v && String(v).trim()).length === 0 && (
                                                                                <span className="text-secondary fst-italic small">No text answers.</span>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {q.type === 'radio' && chartData.length > 0 && (
                                                                        <ResponsiveContainer width="100%" height={280}>
                                                                            <PieChart>
                                                                                <Pie data={chartData} cx="40%" cy="50%" outerRadius={100} dataKey="value"
                                                                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                                                                                    {chartData.map((entry, i) => (
                                                                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                                                    ))}
                                                                                </Pie>
                                                                                <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" />
                                                                            </PieChart>
                                                                        </ResponsiveContainer>
                                                                    )}

                                                                    {q.type === 'checkbox' && chartData.length > 0 && (
                                                                        <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 52)}>
                                                                            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 10, bottom: 0 }}>
                                                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                                                                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                                                                                <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                <Bar dataKey="value" fill={GOLD} radius={[0, 4, 4, 0]}
                                                                                    label={{ position: 'right', fontSize: 12, formatter: (v) => `${v} (${((v / responseCount) * 100).toFixed(0)}%)` }}
                                                                                />
                                                                            </BarChart>
                                                                        </ResponsiveContainer>
                                                                    )}

                                                                    {q.type === 'linear_scale' && chartData.length > 0 && (
                                                                        <ResponsiveContainer width="100%" height={260}>
                                                                            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                                                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                                <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                                                                                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                                                                <RcTooltip formatter={(v) => [`${v} (${((v / responseCount) * 100).toFixed(0)}%)`, 'Responses']} />
                                                                                <Bar dataKey="value" fill={GOLD} radius={[4, 4, 0, 0]}
                                                                                    label={{ position: 'top', fontSize: 11, formatter: (v) => v > 0 ? `${v} (${((v / responseCount) * 100).toFixed(0)}%)` : '' }}
                                                                                />
                                                                            </BarChart>
                                                                        </ResponsiveContainer>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeTab === 'feedback' && (
                                            <div>
                                                {/* Form sub-tabs */}
                                                <div className="d-flex gap-2 mb-4">
                                                    <button
                                                        className={`btn btn-sm fw-semibold px-3 py-2 ${formSubTab === 'feedback' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                                        onClick={() => setFormSubTab('feedback')}
                                                    >
                                                        <i className="bi bi-chat-square-text me-1"></i>Feedback Form
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm fw-semibold px-3 py-2 ${formSubTab === 'registration' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                                        onClick={() => setFormSubTab('registration')}
                                                    >
                                                        <i className="bi bi-person-lines-fill me-1"></i>Registration Form
                                                    </button>
                                                </div>

                                                {formSubTab === 'feedback' && (
                                                    <div className="d-flex flex-column gap-4">
                                                        {/* Feedback Form Image */}
                                                        <div>
                                                            <label className="form-label text-secondary fw-semibold mb-2">Form Image</label>
                                                            {formData.feedbackFormImage ? (
                                                                <img
                                                                    src={config.EVENT_ENDPOINT + 'event/image/' + formData.feedbackFormImage}
                                                                    alt="Feedback form"
                                                                    className="img-thumbnail d-block"
                                                                    style={{ maxHeight: '220px', objectFit: 'contain' }}
                                                                />
                                                            ) : (
                                                                <div className="text-secondary fst-italic">No image uploaded</div>
                                                            )}
                                                        </div>

                                                        {/* Description */}
                                                        <div>
                                                            <label className="form-label text-secondary fw-semibold mb-2">Form Description</label>
                                                            <textarea
                                                                value={formData.feedbackFormDescription}
                                                                readOnly
                                                                className="form-control bg-light"
                                                                rows="3"
                                                                placeholder="No description"
                                                            />
                                                        </div>

                                                        {/* Questions */}
                                                        <div>
                                                            <label className="form-label text-secondary fw-semibold mb-2">
                                                                Custom Questions
                                                                <span className="ms-2 badge bg-secondary fw-normal">
                                                                    {(formData.feedbackFormQuestions || []).length}
                                                                </span>
                                                            </label>
                                                            {(formData.feedbackFormQuestions || []).length === 0 ? (
                                                                <div className="text-secondary fst-italic">No custom questions added.</div>
                                                            ) : (
                                                                <div className="d-flex flex-column gap-3">
                                                                    {(formData.feedbackFormQuestions || []).map((q, idx) => (
                                                                        <div key={idx} className="border rounded-3 p-3 bg-light">
                                                                            <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                                                                <div className="d-flex align-items-start gap-2 flex-grow-1">
                                                                                    <span className="badge bg-warning text-dark flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}>
                                                                                        {idx + 1}
                                                                                    </span>
                                                                                    <span className="fw-semibold text-dark">
                                                                                        {q.question || <span className="fst-italic text-secondary">(No question text)</span>}
                                                                                        {q.required && <span className="text-danger ms-1">*</span>}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                                                                    {q.type === 'short_text' ? 'Short Text' : q.type === 'radio' ? 'Multiple Choice' : q.type === 'checkbox' ? 'Checkbox' : 'Linear Scale'}
                                                                                </span>
                                                                            </div>
                                                                            {(q.type === 'radio' || q.type === 'checkbox') && (
                                                                                <ul className="mb-0 ps-4 mt-1">
                                                                                    {(q.options || []).length === 0
                                                                                        ? <li className="text-secondary small fst-italic">No options</li>
                                                                                        : (q.options || []).map((opt, i) => (
                                                                                            <li key={i} className="text-secondary small">{opt || <span className="fst-italic">(empty)</span>}</li>
                                                                                        ))
                                                                                    }
                                                                                </ul>
                                                                            )}
                                                                            {q.type === 'linear_scale' && (
                                                                                <div className="d-flex gap-2 mt-2 flex-wrap">
                                                                                    {Array.from({ length: (q.scaleMax ?? 5) - (q.scaleMin ?? 1) + 1 }, (_, i) => (q.scaleMin ?? 1) + i).map(n => (
                                                                                        <span key={n} className="badge border text-secondary bg-white" style={{ fontSize: '0.8rem', width: '32px' }}>{n}</span>
                                                                                    ))}
                                                                                    {(q.scaleMinLabel || q.scaleMaxLabel) && (
                                                                                        <div className="w-100 d-flex justify-content-between text-secondary" style={{ fontSize: '0.72rem' }}>
                                                                                            <span>{q.scaleMinLabel}</span>
                                                                                            <span>{q.scaleMaxLabel}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            {q.type === 'short_text' && (
                                                                                <div className="border rounded-2 bg-white text-secondary small px-3 py-2 mt-2" style={{ minHeight: '48px', fontStyle: 'italic' }}>
                                                                                    Text answer...
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {formSubTab === 'registration' && (
                                                    <div className="d-flex flex-column gap-4">
                                                        {/* Description */}
                                                        <div>
                                                            <label className="form-label text-secondary fw-semibold mb-2">Form Description</label>
                                                            <textarea
                                                                value={formData.registrationFormDescription || ''}
                                                                readOnly
                                                                className="form-control bg-light"
                                                                rows="3"
                                                                placeholder="No description"
                                                            />
                                                        </div>

                                                        {/* Custom Questions */}
                                                        <div>
                                                            <label className="form-label text-secondary fw-semibold mb-2">
                                                                Custom Questions
                                                                <span className="ms-2 badge bg-secondary fw-normal">
                                                                    {(formData.registrationFormQuestions || []).length}
                                                                </span>
                                                            </label>
                                                            {(formData.registrationFormQuestions || []).length === 0 ? (
                                                                <div className="text-secondary fst-italic">No custom questions added.</div>
                                                            ) : (
                                                                <div className="d-flex flex-column gap-3">
                                                                    {(formData.registrationFormQuestions || []).map((q, idx) => (
                                                                        <div key={idx} className="border rounded-3 p-3 bg-light">
                                                                            <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                                                                <div className="d-flex align-items-start gap-2 flex-grow-1">
                                                                                    <span className="badge bg-warning text-dark flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}>
                                                                                        {idx + 1}
                                                                                    </span>
                                                                                    <span className="fw-semibold text-dark">
                                                                                        {q.question || <span className="fst-italic text-secondary">(No question text)</span>}
                                                                                        {q.required && <span className="text-danger ms-1">*</span>}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                                                                    {q.type === 'short_text' ? 'Short Text' : q.type === 'radio' ? 'Multiple Choice' : q.type === 'checkbox' ? 'Checkbox' : 'Linear Scale'}
                                                                                </span>
                                                                            </div>
                                                                            {(q.type === 'radio' || q.type === 'checkbox') && (
                                                                                <ul className="mb-0 ps-4 mt-1">
                                                                                    {(q.options || []).length === 0
                                                                                        ? <li className="text-secondary small fst-italic">No options</li>
                                                                                        : (q.options || []).map((opt, i) => (
                                                                                            <li key={i} className="text-secondary small">{opt || <span className="fst-italic">(empty)</span>}</li>
                                                                                        ))
                                                                                    }
                                                                                </ul>
                                                                            )}
                                                                            {q.type === 'linear_scale' && (
                                                                                <div className="d-flex gap-2 mt-2 flex-wrap">
                                                                                    {Array.from({ length: (q.scaleMax ?? 5) - (q.scaleMin ?? 1) + 1 }, (_, i) => (q.scaleMin ?? 1) + i).map(n => (
                                                                                        <span key={n} className="badge border text-secondary bg-white" style={{ fontSize: '0.8rem', width: '32px' }}>{n}</span>
                                                                                    ))}
                                                                                    {(q.scaleMinLabel || q.scaleMaxLabel) && (
                                                                                        <div className="w-100 d-flex justify-content-between text-secondary" style={{ fontSize: '0.72rem' }}>
                                                                                            <span>{q.scaleMinLabel}</span>
                                                                                            <span>{q.scaleMaxLabel}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            {q.type === 'short_text' && (
                                                                                <div className="border rounded-2 bg-white text-secondary small px-3 py-2 mt-2" style={{ minHeight: '48px', fontStyle: 'italic' }}>
                                                                                    Text answer...
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </form>
                                </div>

                                {/* Section Tab Toggle */}
                                <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
                                    <button
                                        className={`btn fw-semibold px-4 py-2 ${sectionTab === 'participants' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                        onClick={() => setSectionTab('participants')}
                                    >
                                        <i className="bi bi-people-fill me-2"></i>List of Participants
                                    </button>
                                    <button
                                        className={`btn fw-semibold px-4 py-2 ${sectionTab === 'feedback' ? 'bg-gold text-dark border-0' : 'btn-outline-secondary'}`}
                                        onClick={() => setSectionTab('feedback')}
                                    >
                                        <i className="bi bi-chat-square-text-fill me-2"></i>List of Feedback
                                    </button>
                                </div>

                                {sectionTab === 'participants' && <>
                                    <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
                                        <div className="d-flex flex-column flex-sm-row gap-2">
                                            <div className="input-group input-search">
                                                <button className="btn bg-white" type="button" onClick={() => setTableSearch('')}>
                                                    {tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}
                                                </button>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder="Search participants"
                                                    value={tableSearch}
                                                    onChange={(e) => setTableSearch(e.target.value)}
                                                />
                                            </div>
                                            <select
                                                className="form-select bg-white"
                                                style={{ cursor: 'pointer' }}
                                                value={statusFilter}
                                                onChange={(e) => {
                                                    setStatusFilter(e.target.value);
                                                    setPagination(prev => ({ ...prev, page: 1 }));
                                                }}
                                            >
                                                <option value="">No Filter (All Status)</option>
                                                <option value="Waiting">Pending Approval</option>
                                                <option value="Accepted">Accepted</option>
                                                <option value="Decline">Declined</option>
                                                <option value="Absent">Absent</option>
                                                <option value="Present">Present</option>
                                            </select>
                                        </div>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {selectedParticipants.length > 0 && (
                                                <div className="d-flex gap-2">
                                                    {!hasProcessedSelected && (
                                                        <>
                                                            <button className="btn btn-success fw-semibold border-0 d-flex align-items-center gap-2" onClick={() => handleBulkAction('approve')}>
                                                                <i className="bi bi-check-lg"></i> Approve ({selectedParticipants.length})
                                                            </button>
                                                            <button className="btn btn-danger fw-semibold border-0 d-flex align-items-center gap-2" onClick={() => handleBulkAction('reject')}>
                                                                <i className="bi bi-x-lg"></i> Reject ({selectedParticipants.length})
                                                            </button>
                                                        </>
                                                    )}
                                                    {areAllSelectedPresent && (
                                                        <button className="btn btn-primary fw-semibold border-0 d-flex align-items-center gap-2" onClick={() => handleBulkAction('feedback')}>
                                                            <i className="bi bi-send"></i> Send Feedback ({selectedParticipants.length})
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <button onClick={() => handleExportData()} className="btn bg-gold text-dark fw-semibold border-0 d-flex align-items-center gap-2">
                                                <i className="bi bi-download"></i> Export Data
                                            </button>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-3 shadow-sm p-4">

                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle">
                                                <thead>
                                                    <tr>
                                                        <th className="bg-gold fw-bold border-0" style={{ borderTopLeftRadius: '8px' }}>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                onChange={handleSelectAll}
                                                                checked={
                                                                    participants.length > 0 && (
                                                                        (participants.some(p => p.allowAttend === null) &&
                                                                            participants.filter(p => p.allowAttend === null).every(p => selectedParticipants.includes(p.id)))
                                                                        ||
                                                                        (!participants.some(p => p.allowAttend === null) && participants.some(p => p.allowAttend === true) &&
                                                                            participants.filter(p => p.allowAttend === true).every(p => selectedParticipants.includes(p.id)))
                                                                    ) && selectedParticipants.length > 0
                                                                }
                                                            />
                                                        </th>
                                                        <th className="bg-gold fw-bold border-0">#</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Name</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Company Name</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Salutation</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Email</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Status</th>
                                                        <th className="bg-gold fw-bold border-0 text-start">Attendance</th>
                                                        <th className="bg-gold fw-bold border-0 text-center">Feedback</th>
                                                        {crmUser.user_level !== 'View' && (
                                                            <th className="bg-gold fw-bold border-0 text-start" style={{ borderTopRightRadius: '8px' }}>Action</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {participants.map((p, idx) => {
                                                        const isProcessed = p.allowAttend !== null;

                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <input
                                                                        disabled={p.allowAttend === false}
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={selectedParticipants.includes(p.id)}
                                                                        onChange={(e) => handleSelectParticipant(e, p.id)}
                                                                    />
                                                                </td>
                                                                <td>{((pagination.page - 1) * pagination.limit) + idx + 1}</td>
                                                                <td>{p.name}</td>
                                                                <td>{p.company}</td>
                                                                <td className="text-start">{p.gender}</td>
                                                                <td className="text-start">{p.email}</td>
                                                                <td className="text-start text-secondary">{p.allowAttend ? 'Accepted' : p.allowAttend === null ? 'Pending Approval' : 'Declined'}</td>
                                                                <td className="text-start text-secondary">{p.attended ? 'Present' : p.attended === null ? 'Waiting' : 'Absent'}</td>
                                                                <td className="text-start text-secondary text-center">{p.sendingFeedback}</td>
                                                                {crmUser.user_level !== 'View' && (
                                                                    <td className="text-start">
                                                                        <div className="d-flex justify-content-start gap-2">
                                                                            <button
                                                                                className="btn btn-sm bg-gold text-dark border-0 p-1"
                                                                                style={{ width: '28px', height: '28px' }}
                                                                                onClick={() => navigate(`/event/detail/${id}/participant/${p.id}`)}
                                                                                title="View Details"
                                                                            >
                                                                                <i className="bi bi-eye-fill"></i>
                                                                            </button>
                                                                            {Array.isArray(p.customAnswers) && p.customAnswers.length > 0 && (
                                                                                <button
                                                                                    className="btn btn-sm btn-info border-0 text-white p-1"
                                                                                    style={{ width: '28px', height: '28px' }}
                                                                                    onClick={() => setSelectedParticipantAnswers({ name: p.name, answers: p.customAnswers })}
                                                                                    title="View Registration Answers"
                                                                                >
                                                                                    <i className="bi bi-card-list"></i>
                                                                                </button>
                                                                            )}
                                                                            {!isProcessed && (
                                                                                <>
                                                                                    <button
                                                                                        className="btn btn-sm btn-success border-0 text-white p-1"
                                                                                        style={{ width: '28px', height: '28px' }}
                                                                                        onClick={() => handleSingleAction('approve', p.id)}
                                                                                        title="Approve"
                                                                                    >
                                                                                        <i className="bi bi-check-lg"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-sm btn-danger border-0 text-white p-1"
                                                                                        style={{ width: '28px', height: '28px' }}
                                                                                        onClick={() => handleSingleAction('reject', p.id)}
                                                                                        title="Reject"
                                                                                    >
                                                                                        <i className="bi bi-x-lg"></i>
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                            {formData.checkInManual && p.allowAttend === true && p.attended !== true && (
                                                                                <button
                                                                                    className="btn btn-sm btn-warning border-0 text-dark p-1"
                                                                                    style={{ width: '28px', height: '28px' }}
                                                                                    onClick={() => handleSingleAction('checkin', p.id)}
                                                                                    title="Manual Check-In"
                                                                                >
                                                                                    <i className="bi bi-person-check-fill"></i>
                                                                                </button>
                                                                            )}
                                                                            {p.allowAttend === true && p.attended === true && (
                                                                                <button
                                                                                    className="btn btn-sm btn-primary border-0 text-white p-1"
                                                                                    style={{ width: '28px', height: '28px' }}
                                                                                    onClick={() => handleSingleAction('feedback', p.id)}
                                                                                    title="Send Feedback"
                                                                                >
                                                                                    <i className="bi bi-send"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination matching Event.js */}
                                        {pagination.totalPages > 0 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="d-flex align-items-center gap-2 text-secondary">
                                                    <small>Show</small>
                                                    <select
                                                        className="form-select form-select-sm border-secondary text-secondary"
                                                        style={{ width: '70px', borderRadius: '4px' }}
                                                        value={pagination.limit}
                                                        onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                    </select>
                                                    <small>entries</small>
                                                </div>
                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0 gap-1">
                                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                                                                <i className="bi bi-chevron-left" />
                                                            </button>
                                                        </li>
                                                        {renderPaginationItems()}
                                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                                                <i className="bi bi-chevron-right" />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </div>
                                </>}

                                {/* Feedback List Section */}
                                {sectionTab === 'feedback' && (
                                    <>
                                        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
                                            <div className="input-group input-search">
                                                <button className="btn bg-white" type="button" onClick={() => setFeedbackSearch('')}>
                                                    {feedbackSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}
                                                </button>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder="Search feedback..."
                                                    value={feedbackSearch}
                                                    onChange={(e) => setFeedbackSearch(e.target.value)}
                                                />
                                            </div>
                                            <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                                <span className="text-secondary small">{feedbackPagination.total} response(s)</span>
                                                <button onClick={handleExportFeedbackData} className="btn bg-gold text-dark fw-semibold border-0 d-flex align-items-center gap-2">
                                                    <i className="bi bi-download"></i> Export Data
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-3 shadow-sm p-4">
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th className="bg-gold fw-bold border-0" style={{ borderTopLeftRadius: '8px' }}>#</th>
                                                            <th className="bg-gold fw-bold border-0">Name</th>
                                                            <th className="bg-gold fw-bold border-0">Email</th>
                                                            <th className="bg-gold fw-bold border-0">Company</th>
                                                            <th className="bg-gold fw-bold border-0">Org Level</th>
                                                            <th className="bg-gold fw-bold border-0">Position</th>
                                                            <th className="bg-gold fw-bold border-0">Submitted At</th>
                                                            <th className="bg-gold fw-bold border-0 text-center" style={{ borderTopRightRadius: '8px' }}>Detail</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {isFeedbackLoading ? (
                                                            <tr><td colSpan={8} className="text-center py-4 text-secondary">Loading...</td></tr>
                                                        ) : feedbacks.length === 0 ? (
                                                            <tr><td colSpan={8} className="text-center py-4 text-secondary fst-italic">No feedback submitted yet.</td></tr>
                                                        ) : feedbacks.map((fb, idx) => (
                                                            <tr key={fb.id} style={{ cursor: 'pointer' }} onClick={() => openFeedbackDetail(fb.id)}>
                                                                <td>{((feedbackPagination.page - 1) * feedbackPagination.limit) + idx + 1}</td>
                                                                <td>{fb.name || '-'}</td>
                                                                <td>{fb.email}</td>
                                                                <td>{fb.companyName || '-'}</td>
                                                                <td>{fb.orgLevel || '-'}</td>
                                                                <td>{fb.position || '-'}</td>
                                                                <td>{fb.createdAt ? new Date(fb.createdAt).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</td>
                                                                <td className="text-center">
                                                                    <button
                                                                        className="btn btn-sm bg-gold text-dark border-0 p-1"
                                                                        style={{ width: '28px', height: '28px' }}
                                                                        onClick={(e) => { e.stopPropagation(); openFeedbackDetail(fb.id); }}
                                                                        title="View Detail"
                                                                    >
                                                                        <i className="bi bi-eye-fill"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {feedbackPagination.totalPages > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mt-3">
                                                    <div className="d-flex align-items-center gap-2 text-secondary">
                                                        <small>Show</small>
                                                        <select
                                                            className="form-select form-select-sm border-secondary text-secondary"
                                                            style={{ width: '70px', borderRadius: '4px' }}
                                                            value={feedbackPagination.limit}
                                                            onChange={(e) => setFeedbackPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                                                        >
                                                            <option value={10}>10</option>
                                                            <option value={20}>20</option>
                                                            <option value={50}>50</option>
                                                        </select>
                                                        <small>entries</small>
                                                    </div>
                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0 gap-1">
                                                            <li className={`page-item ${feedbackPagination.page === 1 ? 'disabled' : ''}`}>
                                                                <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setFeedbackPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={feedbackPagination.page === 1}>
                                                                    <i className="bi bi-chevron-left" />
                                                                </button>
                                                            </li>
                                                            {renderFeedbackPaginationItems()}
                                                            <li className={`page-item ${feedbackPagination.page === feedbackPagination.totalPages ? 'disabled' : ''}`}>
                                                                <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setFeedbackPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={feedbackPagination.page === feedbackPagination.totalPages}>
                                                                    <i className="bi bi-chevron-right" />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Detail Modal */}
            <Modal show={showFeedbackModal} onHide={() => { setShowFeedbackModal(false); setSelectedFeedback(null); }} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5">Feedback Detail</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {selectedFeedback && (
                        <div className="d-flex flex-column gap-4">
                            {/* Respondent Info */}
                            <div>
                                <p className="text-secondary fw-semibold small mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>Respondent Info</p>
                                <div className="row g-3">
                                    {[
                                        { label: 'Name', value: selectedFeedback.name },
                                        { label: 'Email', value: selectedFeedback.email },
                                        { label: 'Phone', value: selectedFeedback.phone },
                                        { label: 'Company', value: selectedFeedback.companyName },
                                        { label: 'Org Level', value: selectedFeedback.orgLevel },
                                        { label: 'Position', value: selectedFeedback.position },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="col-md-6">
                                            <div className="p-3 bg-light rounded-3">
                                                <div className="text-secondary small mb-1">{label}</div>
                                                <div className="fw-semibold text-dark">{value || <span className="fst-italic text-secondary">-</span>}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Answers */}
                            {Array.isArray(selectedFeedback.answers) && selectedFeedback.answers.length > 0 && (
                                <div>
                                    <p className="text-secondary fw-semibold small mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>Answers</p>
                                    <div className="d-flex flex-column gap-3">
                                        {selectedFeedback.answers.map((ans, idx) => (
                                            <div key={idx} className="border rounded-3 p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                                    <div className="d-flex gap-2 align-items-start">
                                                        <span className="badge bg-warning text-dark flex-shrink-0" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>{idx + 1}</span>
                                                        <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>{ans.question || '(No question text)'}</span>
                                                    </div>
                                                    <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                                        {ans.type === 'short_text' ? 'Short Text' : ans.type === 'radio' ? 'Multiple Choice' : ans.type === 'checkbox' ? 'Checkbox' : ans.type === 'linear_scale' ? 'Linear Scale' : ans.type}
                                                    </span>
                                                </div>
                                                <div className="ps-4">
                                                    {Array.isArray(ans.value)
                                                        ? ans.value.length === 0
                                                            ? <span className="text-secondary fst-italic small">No answer</span>
                                                            : <div className="d-flex flex-wrap gap-2">{ans.value.map((v, i) => <span key={i} className="badge bg-gold text-dark fw-normal px-3 py-2">{v}</span>)}</div>
                                                        : ans.value === '' || ans.value === null || ans.value === undefined
                                                            ? <span className="text-secondary fst-italic small">No answer</span>
                                                            : <span className="badge bg-gold text-dark fw-normal px-3 py-2">{String(ans.value)}</span>
                                                    }
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="text-secondary small text-end">
                                Submitted: {selectedFeedback.createdAt ? new Date(selectedFeedback.createdAt).toLocaleString('id-ID', { dateStyle: 'long', timeStyle: 'short' }) : '-'}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <button className="btn btn-light fw-semibold" onClick={() => { setShowFeedbackModal(false); setSelectedFeedback(null); }}>Close</button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5">
                        Confirm Action
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirmActionType === 'approve' && (
                        <p>Are you sure you want to approve {actionParticipantIds.length} participant(s) for this event?</p>
                    )}
                    {confirmActionType === 'reject' && (
                        <p>Are you sure you want to reject {actionParticipantIds.length} participant(s) from this event?</p>
                    )}
                    {confirmActionType === 'feedback' && (
                        <p>Are you sure you want to send a feedback form to {actionParticipantIds.length} participant(s)?</p>
                    )}
                    {confirmActionType === 'checkin' && (
                        <p>Mark {actionParticipantIds.length} participant(s) as present (manual check-in)?</p>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <button
                        className="btn btn-light fw-semibold"
                        onClick={() => setShowConfirmModal(false)}
                        disabled={isActionLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className={`btn fw-semibold text-white d-flex align-items-center justify-content-center gap-2 ${confirmActionType === 'approve' ? 'btn-success' : confirmActionType === 'reject' ? 'btn-danger' : confirmActionType === 'checkin' ? 'btn-warning' : 'btn-primary'}`}
                        onClick={executeConfirmAction}
                        disabled={isActionLoading}
                    >
                        {isActionLoading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                        Confirm
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Registration Custom Answers Modal */}
            <Modal show={!!selectedParticipantAnswers} onHide={() => setSelectedParticipantAnswers(null)} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5">
                        <i className="bi bi-card-list me-2 text-info"></i>
                        Registration Answers — {selectedParticipantAnswers?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    {(selectedParticipantAnswers?.answers || []).length === 0 ? (
                        <div className="text-secondary fst-italic text-center py-4">No answers recorded.</div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {(selectedParticipantAnswers?.answers || []).map((a, idx) => (
                                <div key={idx} className="border rounded-3 p-3 bg-light">
                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                        <div className="d-flex gap-2 align-items-start">
                                            <span className="badge bg-warning text-dark flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '0.75rem' }}>
                                                {idx + 1}
                                            </span>
                                            <span className="fw-semibold text-dark">{a.question}</span>
                                        </div>
                                        <span className="badge bg-secondary text-white flex-shrink-0" style={{ fontSize: '0.7rem' }}>
                                            {a.type === 'short_text' ? 'Short Text' : a.type === 'radio' ? 'Multiple Choice' : a.type === 'checkbox' ? 'Checkbox' : 'Linear Scale'}
                                        </span>
                                    </div>
                                    <div className="ms-4 ps-2">
                                        {Array.isArray(a.value) ? (
                                            a.value.length === 0
                                                ? <span className="text-secondary fst-italic small">No answer</span>
                                                : <div className="d-flex flex-wrap gap-1">
                                                    {a.value.map((v, i) => (
                                                        <span key={i} className="badge bg-info text-white px-2 py-1" style={{ fontSize: '0.8rem' }}>{v}</span>
                                                    ))}
                                                </div>
                                        ) : (
                                            a.value === '' || a.value === null || a.value === undefined
                                                ? <span className="text-secondary fst-italic small">No answer</span>
                                                : <span className="text-dark">{String(a.value)}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <button className="btn btn-light fw-semibold" onClick={() => setSelectedParticipantAnswers(null)}>Close</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DetailEvent;



